import type { Dayjs } from 'dayjs';
import type { IInvoiceItem } from 'src/types/invoice';
import type { Order, STATUS, Product, OrderLine } from 'src/types/types';

import dayjs from 'dayjs';
import { sumBy } from 'es-toolkit';
import { useMemo, useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import { inputBaseClasses } from '@mui/material/InputBase';
import { Switch, Tooltip, TextField, Autocomplete, FormControlLabel } from '@mui/material';

import { getGuid } from 'src/utils/utils';
import { fDate } from 'src/utils/format-time';
import { CONFIRMED_ORDER_STATUSES, getReplacedQuantityByProductId } from 'src/utils/order-stock';

import { useAppSelector } from 'src/hooks';
import { INVOICE_SERVICE_OPTIONS } from 'src/_mock';

import { Iconify } from 'src/components/iconify';

import { InvoiceTotalSummary } from './invoice-total-summary';

export const groupKey = (category: string) =>
  ['borde', 'stole'].includes(category) ? 'moebler' : category;

export const formatGroupName = (group: string) => {
  const mapping: Record<string, string> = {
    moebler: 'Borde og stole',
    service: 'Service',
    telte: 'Telte',
    tekstil: 'Tekstil',
    andet: 'Andet',
    erstatning: 'Erstatning',
  };
  return mapping[group] || group;
};

const categoryOrder = ['moebler', 'service', 'telte', 'tekstil', 'andet', 'erstatning'];

export type ProductReservation = {
  orderId: string;
  customerName: string;
  quantity: number;
  startDate: number;
  endDate: number;
};

// Genanvendelig lagertilgængeligheds-beregning for en periode: hvor meget af hvert
// produkt der reelt er tilbage, når der tages højde for registrerede erstatninger
// (samme beregning som produktlisten bruger), andre ordrers reservationer i perioden,
// og de øvrige linjer i den ordre, der redigeres nu.
export function useProductAvailability(
  orders: Order[],
  orderLines: OrderLine[],
  startDate: Dayjs,
  endDate: Dayjs,
  currentOrderId?: string
) {
  const replacedQuantityByProductId = useMemo(
    () => getReplacedQuantityByProductId(orders),
    [orders]
  );

  // Hvilke andre ordrer der allerede har lagt fast på hvert produkt,
  // i perioder der overlapper med den periode, der redigeres nu (hele kalenderdage).
  const reservationsByProductId = useMemo(() => {
    const map = new Map<number, ProductReservation[]>();
    const rangeStart = startDate.startOf('day').unix();
    const rangeEnd = endDate.endOf('day').unix();

    orders
      .filter((order) => order.id !== currentOrderId)
      .filter((order) => CONFIRMED_ORDER_STATUSES.includes(order.status as STATUS))
      .filter((order) => {
        const orderStart = dayjs.unix(order.startDate).startOf('day').unix();
        const orderEnd = dayjs.unix(order.endDate).endOf('day').unix();
        return orderStart <= rangeEnd && orderEnd >= rangeStart;
      })
      .forEach((order) => {
        order.orderLines.forEach((line) => {
          if (!line.product || line.product.category === 'erstatning') return;
          const reservations = map.get(line.product.id) ?? [];
          reservations.push({
            orderId: order.id,
            customerName: order.customer?.name || 'Ukendt kunde',
            quantity: line.quantity,
            startDate: order.startDate,
            endDate: order.endDate,
          });
          map.set(line.product.id, reservations);
        });
      });

    return map;
  }, [orders, currentOrderId, startDate, endDate]);

  // Erstatningsprodukter er ikke fysisk lager, der bookes over en periode,
  // så der beregnes ikke reservationer for dem.
  const getReservationsInPeriod = (product: Product): ProductReservation[] =>
    product.category === 'erstatning' ? [] : (reservationsByProductId.get(product.id) ?? []);

  const getAvailableInPeriod = (product: Product, excludeOrderLineId?: string): number | null => {
    if (product.category === 'erstatning') return null;

    const replacedQuantity = product.replacementProduct
      ? (replacedQuantityByProductId.get(product.replacementProduct.id) ?? 0)
      : 0;
    const effectiveAvailable = Math.max(product.available - replacedQuantity, 0);

    const reservedByOtherOrders = sumBy(getReservationsInPeriod(product), (r) => r.quantity);
    const reservedByOwnLines = sumBy(
      orderLines.filter(
        (line) => line.product?.id === product.id && line.orderLineId !== excludeOrderLineId
      ),
      (line) => line.quantity
    );

    return Math.max(effectiveAvailable - reservedByOtherOrders - reservedByOwnLines, 0);
  };

  return { getAvailableInPeriod, getReservationsInPeriod };
}

export const defaultItem: Omit<IInvoiceItem, 'id'> = {
  title: '',
  description: '',
  service: INVOICE_SERVICE_OPTIONS[0].name,
  price: INVOICE_SERVICE_OPTIONS[0].price,
  quantity: 1,
  total: 0,
};

const formatPrice = (price: number) =>
  new Intl.NumberFormat('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    price
  );

interface Props {
  orderLines: OrderLine[];
  setOrderLines: (orderLines: OrderLine[]) => void;
  deliveryPrice: null | number;
  setDeliveryPrice: (price: number) => void;
  hasDelivery: boolean;
  setHasDelivery: (val: boolean) => void;
  hasMoms: boolean;
  setHasMoms: (val: boolean) => void;
  startDate: Dayjs;
  endDate: Dayjs;
  currentOrderId?: string;
}

export function InvoiceNewEditDetails({
  orderLines,
  setOrderLines,
  deliveryPrice,
  setDeliveryPrice,
  hasDelivery,
  setHasDelivery,
  hasMoms,
  setHasMoms,
  startDate,
  endDate,
  currentOrderId,
}: Props) {
  const discount = 0;
  const products = useAppSelector((state) => state.products.products);
  const orders = useAppSelector((state) => state.orders.orders);
  const [deliveryPriceInput, setDeliveryPriceInput] = useState('');

  const { getAvailableInPeriod, getReservationsInPeriod } = useProductAvailability(
    orders,
    orderLines,
    startDate,
    endDate,
    currentOrderId
  );

  const subtotal = sumBy(orderLines, (item) => item?.quantity * item?.price);
  const totalAmount = subtotal - discount + (deliveryPrice ?? 0);

  useEffect(() => {
    if (!hasDelivery) {
      setDeliveryPrice(0);
    }
  }, [hasDelivery, setDeliveryPrice]);

  useEffect(() => {
    setDeliveryPriceInput(deliveryPrice?.toString().replace('.', ',') ?? '');
  }, [deliveryPrice]);

  const addOrderLine = () => {
    const newLine: OrderLine = {
      name: '',
      price: 0,
      quantity: 1,
      orderLineId: getGuid(),
      product: undefined,
    };
    setOrderLines([...orderLines, newLine]);
  };

  const orderLineUpdate = (orderLine: OrderLine) => {
    setOrderLines(
      orderLines.map((obj) => (orderLine.orderLineId === obj.orderLineId ? orderLine : obj))
    );
  };

  const removeOrderLine = (orderLineId: string) => {
    setOrderLines(orderLines.filter((obj) => obj.orderLineId !== orderLineId));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ color: 'text.disabled', mb: 3 }}>
        Tilbud
      </Typography>

      <Stack divider={<Divider flexItem sx={{ borderStyle: 'dashed' }} />} spacing={3}>
        {orderLines.map((orderLine) => (
          <InvoiceItem
            key={orderLine.orderLineId}
            orderLine={orderLine}
            orderLineUpdate={orderLineUpdate}
            onRemoveOrderLine={removeOrderLine}
            getAvailableInPeriod={getAvailableInPeriod}
            getReservationsInPeriod={getReservationsInPeriod}
          />
        ))}
      </Stack>

      <Divider sx={{ my: 3, borderStyle: 'dashed' }} />

      <Box
        sx={{
          gap: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-end', md: 'center' },
        }}
      >
        <Button
          size="small"
          color="primary"
          startIcon={<Iconify icon="mingcute:add-line" />}
          onClick={addOrderLine}
          sx={{ flexShrink: 0 }}
        >
          Tilføj linje
        </Button>

        <Box
          sx={{
            gap: 2,
            width: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <FormControlLabel
            control={
              <Switch
                id="toggle-moms"
                checked={hasMoms}
                onChange={(e) => setHasMoms(e.target.checked)}
              />
            }
            label="Beregn moms"
          />
          <FormControlLabel
            control={
              <Switch
                id="toggle-taxes"
                checked={hasDelivery}
                onChange={(e) => setHasDelivery(e.target.checked)}
              />
            }
            label="Levering"
          />

          <TextField
            size="small"
            type="text"
            label="Leveringspris"
            value={deliveryPriceInput}
            onChange={(e) => {
              const val = e.target.value;
              setDeliveryPriceInput(val);
              const parsed = parseFloat(val.replace(',', '.'));
              if (!isNaN(parsed)) {
                setDeliveryPrice(parsed);
              }
            }}
            disabled={!hasDelivery}
            sx={{
              width: { md: '20%' },
              [`& .${inputBaseClasses.input}`]: {
                textAlign: { md: 'right' },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ typography: 'subtitle2', color: 'text.disabled' }}>kr.</Box>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      <InvoiceTotalSummary
        shippingEnabled={hasDelivery}
        shipping={deliveryPrice ?? 0}
        subtotal={Number(((subtotal + (deliveryPrice ?? 0)) / 5).toFixed(2))}
        totalAmount={Number(totalAmount.toFixed(2))}
      />
    </Box>
  );
}
export function InvoiceItem({
  orderLine,
  onRemoveOrderLine,
  orderLineUpdate,
  getAvailableInPeriod,
  getReservationsInPeriod,
}: InvoiceItemProps) {
  const products = useAppSelector((state) => state.products.products);

  const [priceInput, setPriceInput] = useState(orderLine.price.toString().replace('.', ','));

  useEffect(() => {
    setPriceInput(orderLine.price.toString().replace('.', ','));
  }, [orderLine.price]);

  const sortedProducts = useMemo(
    () =>
      [...products].sort((a, b) => {
        const groupA = groupKey(a.category);
        const groupB = groupKey(b.category);
        const indexA = categoryOrder.indexOf(groupA);
        const indexB = categoryOrder.indexOf(groupB);
        return indexA !== indexB ? indexA - indexB : (a.order ?? 0) - (b.order ?? 0);
      }),
    [products]
  );

  const handleSelectService = (product: Product) => {
    orderLineUpdate({
      ...orderLine,
      name: product.name,
      price: product.price,
      quantity: 1,
      product,
    });
  };

  const updateQuantity = (quantity: number) => {
    orderLineUpdate({ ...orderLine, quantity });
  };

  const updatePrice = (price: number) => {
    orderLineUpdate({ ...orderLine, price });
  };

  const availableForSelected = orderLine.product
    ? getAvailableInPeriod(orderLine.product, orderLine.orderLineId)
    : null;
  const overbookedBy =
    availableForSelected !== null ? orderLine.quantity - availableForSelected : 0;
  const isOverbooked = availableForSelected !== null && overbookedBy > 0;
  const reservingOrders = orderLine.product ? getReservationsInPeriod(orderLine.product) : [];

  return (
    <Box
      sx={{
        gap: 2,
        width: 1,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
      }}
    >
      {isOverbooked && (
        <Tooltip
          arrow
          placement="top"
          slotProps={{ tooltip: { sx: { maxWidth: 'none' } } }}
          title={
            <>
              <Typography
                variant="caption"
                sx={{ display: 'block', fontWeight: 'bold', whiteSpace: 'nowrap' }}
              >
                Kun {availableForSelected} ud af {orderLine.product?.available} stk tilbage i
                perioden
              </Typography>
              {reservingOrders.length > 0 && (
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                  {reservingOrders.map((reservation, index) => (
                    <Typography
                      key={`${reservation.orderId}-${index}`}
                      component="li"
                      variant="caption"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {reservation.customerName} ({fDate(reservation.startDate * 1000)} –{' '}
                      {fDate(reservation.endDate * 1000)}): {reservation.quantity} stk.
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          }
        >
          <Box component="span" sx={{ display: 'inline-flex', cursor: 'default', flexShrink: 0 }}>
            <Iconify icon="solar:danger-triangle-bold" width={18} sx={{ color: 'warning.main' }} />
          </Box>
        </Tooltip>
      )}

      <Autocomplete
        value={orderLine.product}
        options={sortedProducts}
        groupBy={(option) => groupKey(option.category)}
        getOptionLabel={(option) => option.name}
        onChange={(event, newValue) => newValue && handleSelectService(newValue)}
        renderInput={(params) => <TextField {...params} label="Produkt" />}
        disableClearable
        size="small"
        sx={{ width: { xs: 1, md: '40%' } }}
        renderGroup={(params) => (
          <li key={params.key}>
            <Typography sx={{ pl: 0.5, pt: 1, fontWeight: 'bold' }}>
              {formatGroupName(params.group)}
            </Typography>
            <ul>{params.children}</ul>
          </li>
        )}
        renderOption={(props, option) => {
          const remaining = getAvailableInPeriod(option, orderLine.orderLineId);
          return (
            <li {...props} key={option.id}>
              <Box sx={{ display: 'flex', width: 1, justifyContent: 'space-between', gap: 1 }}>
                <span>{option.name}</span>
                {remaining !== null && (
                  <Typography
                    variant="caption"
                    sx={{ color: remaining <= 0 ? 'error.main' : 'text.disabled' }}
                  >
                    {remaining <= 0 ? 'Udsolgt i perioden' : `${remaining} tilbage`}
                  </Typography>
                )}
              </Box>
            </li>
          );
        }}
      />

      <TextField
        size="small"
        type="text"
        label="Antal"
        value={orderLine.quantity.toString()}
        onChange={(e) => {
          const value = e.target.value.replace(',', '.');
          updateQuantity(parseInt(value, 10) || 0);
        }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <Box sx={{ typography: 'subtitle2', color: 'text.disabled' }}>stk.</Box>
              </InputAdornment>
            ),
          },
        }}
        sx={{
          width: { xs: 1, md: '15%' },
          [`& .${inputBaseClasses.input}`]: { textAlign: { md: 'right' } },
        }}
      />

      <TextField
        size="small"
        type="text"
        label="Pris pr stk"
        value={priceInput}
        onChange={(e) => {
          const val = e.target.value;
          setPriceInput(val);
          const parsed = parseFloat(val.replace(',', '.'));
          if (!isNaN(parsed)) {
            updatePrice(parsed);
          }
        }}
        sx={{
          width: { xs: 1, md: '20%' },
          [`& .${inputBaseClasses.input}`]: { textAlign: { md: 'right' } },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Box sx={{ typography: 'subtitle2', color: 'text.disabled' }}>kr.</Box>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        disabled
        size="small"
        type="text"
        label="Pris"
        value={formatPrice(orderLine.price * orderLine.quantity)}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <Box sx={{ typography: 'subtitle2', color: 'text.disabled' }}>kr.</Box>
              </InputAdornment>
            ),
          },
        }}
        sx={{
          width: { xs: 1, md: '20%' },
          [`& .${inputBaseClasses.input}`]: { textAlign: { md: 'right' } },
        }}
      />

      <Button
        size="small"
        color="error"
        startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
        onClick={() => onRemoveOrderLine(orderLine.orderLineId)}
        sx={{
          width: { md: '5%' },
          '& .MuiButton-startIcon': { marginRight: 0 },
        }}
      />
    </Box>
  );
}

type InvoiceItemProps = {
  orderLine: OrderLine;
  onRemoveOrderLine: (orderLineId: string) => void;
  orderLineUpdate: (orderLine: OrderLine) => void;
  getAvailableInPeriod: (product: Product, excludeOrderLineId?: string) => number | null;
  getReservationsInPeriod: (product: Product) => ProductReservation[];
};
